/**
 * Re-train ciblé (réutilisabilité — Étape 6, cf. spec/reusability.md).
 *
 * - RetrainFocusBanner : bandeau « focus verrouillé » affiché PENDANT une séance
 *   de re-train (l'utilisateur sait exactement quel point il retravaille).
 * - FocusDeltaChip : chip de progression au bilan (critère avant → après).
 *   Wording factuel et bienveillant : une baisse n'est jamais un échec, juste un
 *   point encore à travailler (cf. règle « reformuler l'échec, jamais humilier »).
 */
import { useTranslation } from "react-i18next";
import { Target, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { pickScoringLabel } from "../lib/scoring-i18n";

const NAVY = "#384872";
const GREEN = "#1E7A3C";
const CORAL = "#C0423F";

export function RetrainFocusBanner({
  focus,
  lang,
}: {
  focus: { key: string; labelFr: string; labelEn: string; labelSpoken?: string };
  lang: string;
}) {
  const { t } = useTranslation();
  // Helper canonique : il fait passer la langue d'entraînement devant le FR/EN, et
  // la règle de repli ne vit qu'à un seul endroit.
  const label = pickScoringLabel(focus, lang);
  return (
    <div
      className="mb-2 flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-sm"
      style={{ background: "#38487214", border: `1px solid ${NAVY}` }}
    >
      <Target className="h-4 w-4 shrink-0" style={{ color: NAVY }} />
      <span className="font-semibold" style={{ color: NAVY }}>
        {t("retrain.focusBannerTitle")}
      </span>
      <span className="font-medium" style={{ color: NAVY }}>
        {label}
      </span>
      <span className="text-muted-foreground text-xs sm:ml-2">{t("retrain.focusBannerHint")}</span>
    </div>
  );
}

export function FocusDeltaChip({
  focus,
  delta,
  lang,
}: {
  focus: { key: string; labelFr: string; labelEn: string; labelSpoken?: string } | null;
  delta: { before: number | null; after: number | null; delta: number | null };
  lang: string;
}) {
  const { t } = useTranslation();
  if (focus == null || delta.before == null || delta.after == null || delta.delta == null) return null;
  // Helper canonique : il fait passer la langue d'entraînement devant le FR/EN, et
  // la règle de repli ne vit qu'à un seul endroit.
  const label = pickScoringLabel(focus, lang);
  const d = delta.delta;
  const positive = d > 0;
  const negative = d < 0;
  const color = positive ? GREEN : negative ? CORAL : NAVY;
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const sign = positive ? "+" : "";
  const message = positive
    ? t("retrain.deltaProgress")
    : negative
      ? t("retrain.deltaToWork")
      : t("retrain.deltaStable");

  return (
    <div
      className="mb-4 w-full max-w-md rounded-2xl border bg-white p-4 text-left shadow-sm"
      style={{ borderColor: color }}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 shrink-0" style={{ color }} />
        <span className="font-semibold" style={{ color: NAVY, fontFamily: "Sora, sans-serif" }}>
          {label}
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-sm text-muted-foreground">{delta.before}</span>
        <span className="text-sm text-muted-foreground">→</span>
        <span className="text-2xl font-bold" style={{ color: NAVY }}>
          {delta.after}
        </span>
        <span className="ml-1 rounded-full px-2 py-0.5 text-xs font-semibold text-white" style={{ background: color }}>
          {sign}
          {d}
        </span>
      </div>
      <p className="mt-2 text-xs" style={{ color: NAVY }}>
        {message}
      </p>
    </div>
  );
}
